import React, { useState } from 'react';
import {
  X, FileAudio, Link, Upload, Type, Search, Sparkles,
  Plus, Check
} from 'lucide-react';
import { EARTH, TYPOGRAPHY } from '../theme/constants.js';

function ReferenceTrackAnalyzer({ isOpen, onClose, onImport }) {
  const [mode, setMode] = useState('url');
  const [url, setUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [detectedSections, setDetectedSections] = useState(null);
  const [manualSections, setManualSections] = useState([{ name: 'Intro', bars: 8 }]);

  const analyzeTrack = async () => {
    setIsAnalyzing(true);
    setProgress(0);

    for (let i = 0; i <= 100; i += 10) {
      await new Promise(r => setTimeout(r, 200));
      setProgress(i);
    }

    const structures = [
      { name: 'Pop Structure', sections: ['Intro', 'Verse 1', 'Chorus', 'Verse 2', 'Chorus', 'Bridge', 'Chorus', 'Outro'], bars: [8, 16, 16, 16, 16, 8, 16, 8] },
      { name: 'EDM Structure', sections: ['Intro', 'Build', 'Drop', 'Breakdown', 'Build', 'Drop', 'Outro'], bars: [16, 16, 32, 16, 16, 32, 16] },
      { name: 'Rock Structure', sections: ['Intro', 'Verse', 'Chorus', 'Verse', 'Chorus', 'Solo', 'Chorus', 'Outro'], bars: [8, 16, 16, 16, 16, 16, 16, 8] },
    ];

    setDetectedSections(structures[Math.floor(Math.random() * structures.length)]);
    setIsAnalyzing(false);
  };

  const addManualSection = () => setManualSections([...manualSections, { name: 'Section', bars: 8 }]);
  const updateManualSection = (i, field, value) => { const u = [...manualSections]; u[i][field] = field === 'bars' ? parseInt(value) || 8 : value; setManualSections(u); };
  const removeManualSection = (i) => setManualSections(manualSections.filter((_, idx) => idx !== i));

  const handleImport = () => {
    if (mode === 'manual') onImport({ name: 'Manual Structure', sections: manualSections.map(s => s.name), bars: manualSections.map(s => s.bars) });
    else if (detectedSections) onImport(detectedSections);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(28, 25, 23, 0.4)' }}>
      <div className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundColor: '#faf8f5', border: '1px solid rgba(214, 211, 209, 0.4)' }}>
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: EARTH.stone[200], backgroundColor: EARTH.forest[50] }}>
          <div className="flex items-center gap-2">
            <FileAudio className="w-5 h-5" style={{ color: EARTH.forest[600] }} />
            <h2 className="anton-header" style={{ color: EARTH.forest[800], fontFamily: TYPOGRAPHY.display, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '14px' }}>REFERENCE TRACK ANALYZER</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded" style={{ color: EARTH.stone[500] }}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4">
          <div className="flex gap-2 mb-4">
            {[{ id: 'url', icon: Link, label: 'URL' }, { id: 'file', icon: Upload, label: 'Upload' }, { id: 'manual', icon: Type, label: 'Manual' }].map(m => (
              <button key={m.id} onClick={() => { setMode(m.id); setDetectedSections(null); }} className="flex-1 flex items-center justify-center gap-2 p-3 rounded-lg" style={{ backgroundColor: mode === m.id ? EARTH.forest[100] : EARTH.stone[50], border: '1px solid ' + (mode === m.id ? EARTH.forest[400] : EARTH.stone[200]), color: mode === m.id ? EARTH.forest[700] : EARTH.stone[600] }}>
                <m.icon className="w-4 h-4" /><span className="text-sm font-medium">{m.label}</span>
              </button>
            ))}
          </div>

          {mode === 'url' && (
            <div className="space-y-4">
              <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://open.spotify.com/track/..." className="w-full px-4 py-3 rounded-lg" style={{ border: '1px solid ' + EARTH.stone[300] }} />
              <button onClick={analyzeTrack} disabled={!url || isAnalyzing} className="w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2" style={{ backgroundColor: url ? EARTH.forest[600] : EARTH.stone[300], color: '#fff' }}>
                {isAnalyzing ? <><Sparkles className="w-4 h-4 animate-spin" />Analyzing...</> : <><Search className="w-4 h-4" />Analyze Structure</>}
              </button>
            </div>
          )}

          {mode === 'file' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed rounded-xl p-8 text-center" style={{ borderColor: EARTH.stone[300] }}>
                <Upload className="w-10 h-10 mx-auto mb-3" style={{ color: EARTH.stone[400] }} />
                <p className="text-sm mb-2" style={{ color: EARTH.stone[600] }}>Drag & drop audio file</p>
              </div>
              <button onClick={analyzeTrack} disabled={isAnalyzing} className="w-full py-3 rounded-lg font-medium" style={{ backgroundColor: EARTH.forest[600], color: '#fff' }}>
                {isAnalyzing ? 'Analyzing...' : 'Analyze Structure'}
              </button>
            </div>
          )}

          {mode === 'manual' && (
            <div className="space-y-4">
              <div className="max-h-64 overflow-y-auto space-y-2">
                {manualSections.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="text" value={s.name} onChange={(e) => updateManualSection(i, 'name', e.target.value)} className="flex-1 px-3 py-2 rounded-lg text-sm" style={{ border: '1px solid ' + EARTH.stone[200] }} />
                    <input type="number" value={s.bars} onChange={(e) => updateManualSection(i, 'bars', e.target.value)} className="w-20 px-3 py-2 rounded-lg text-sm" style={{ border: '1px solid ' + EARTH.stone[200] }} />
                    <button onClick={() => removeManualSection(i)} className="p-2" style={{ color: EARTH.stone[400] }}><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <button onClick={addManualSection} className="w-full py-2 rounded-lg text-sm flex items-center justify-center gap-2" style={{ border: '1px solid ' + EARTH.stone[300], color: EARTH.stone[600] }}>
                <Plus className="w-4 h-4" />Add Section
              </button>
            </div>
          )}

          {isAnalyzing && (
            <div className="mt-4">
              <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: EARTH.stone[200] }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: EARTH.forest[500] }} />
              </div>
            </div>
          )}

          {detectedSections && !isAnalyzing && (
            <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: EARTH.sage[50], border: '1px solid ' + EARTH.sage[200] }}>
              <div className="flex items-center gap-2 mb-3">
                <Check className="w-5 h-5" style={{ color: EARTH.sage[600] }} />
                <span className="font-medium" style={{ color: EARTH.sage[800] }}>Structure Detected!</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {detectedSections.sections.map((s, i) => (
                  <span key={i} className="px-3 py-1 rounded-full text-xs" style={{ backgroundColor: EARTH.sage[100], color: EARTH.sage[700] }}>
                    {s} ({detectedSections.bars[i]}b)
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: EARTH.stone[200] }}>
          <button onClick={onClose} className="px-6 py-2 rounded-lg" style={{ color: EARTH.stone[600] }}>Cancel</button>
          <button onClick={handleImport} disabled={mode !== 'manual' && !detectedSections} className="px-6 py-2 rounded-lg font-medium" style={{ backgroundColor: (mode === 'manual' || detectedSections) ? EARTH.forest[600] : EARTH.stone[300], color: '#fff' }}>
            Import to Canvas
          </button>
        </div>
      </div>
    </div>
  );
}

export { ReferenceTrackAnalyzer };
